import { motion } from 'framer-motion';
import {
	Truck,
	BatteryCharging,
	CircleDot,
	KeyRound,
	Fuel,
	AnchorIcon,
	ArrowUpRight,
} from 'lucide-react';
import { SERVICES } from '@/constants/testIds';

const ITEMS = [
	{
		slug: 'towing',
		num: '01',
		icon: Truck,
		title: 'Towing',
		copy: 'Light, medium and long-distance towing. Cars, trucks, motorcycles — dispatched to your exact location.',
	},
	{
		slug: 'jump-start',
		num: '02',
		icon: BatteryCharging,
		title: 'Jump Start',
		copy: 'Dead battery in a parking lot or on the shoulder? We roll a portable jump pack to you, day or night.',
	},
	{
		slug: 'flat-tire',
		num: '03',
		icon: CircleDot,
		title: 'Flat Tire',
		copy: 'Spare-tire swap, temporary inflation, or a tow to the nearest tire shop when it can’t be saved roadside.',
	},
	{
		slug: 'lockout',
		num: '04',
		icon: KeyRound,
		title: 'Lockout',
		copy: 'Keys locked inside? Certified operators use non-destructive techniques to get you back behind the wheel.',
	},
	{
		slug: 'fuel-delivery',
		num: '05',
		icon: Fuel,
		title: 'Fuel Delivery',
		copy: 'Ran the tank dry? Emergency fuel drop-off — gas or diesel — brought straight to your vehicle.',
	},
	{
		slug: 'winch-out',
		num: '06',
		icon: AnchorIcon,
		title: 'Winch-Out',
		copy: 'Stuck in mud, snow, sand or a ditch? Heavy-duty winching to pull you out and get you moving again.',
	},
];

export const Services = () => {
	return (
		<section
			id="services"
			data-testid={SERVICES.root}
			className="bg-white"
		>
			<div className="mx-auto max-w-[1400px] px-4 sm:px-8 pt-24 sm:pt-32 pb-6">
				<div className="grid md:grid-cols-12 gap-6 items-end mb-10 sm:mb-14">
					<div className="md:col-span-4">
						<div className="font-chivo text-xs uppercase tracking-[0.3em] text-brand-red font-bold mb-4">
							/ Services 01–06
						</div>
					</div>
					<div className="md:col-span-8">
						<motion.h2
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, margin: '-60px' }}
							transition={{ duration: 0.7 }}
							className="font-anton text-[38px] sm:text-[64px] md:text-[84px] leading-[0.92] text-brand-navy"
						>
							Six ways we get you{' '}
							<span className="text-brand-red">back on the road.</span>
						</motion.h2>
					</div>
				</div>
			</div>

			<div className="mx-auto max-w-[1400px] px-4 sm:px-8 pb-24 sm:pb-32">
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-brand-navy">
					{ITEMS.map((it, i) => (
						<motion.a
							key={it.slug}
							href="tel:+13072224082"
							data-testid={SERVICES.item(it.slug)}
							initial={{ opacity: 0, y: 24 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, margin: '-80px' }}
							transition={{ duration: 0.55, delay: (i % 3) * 0.06 }}
							className="group relative block border-r border-b border-brand-navy p-6 sm:p-8 min-h-[280px] bg-white hover:bg-brand-navy transition-colors duration-300"
						>
							<div className="flex items-start justify-between">
								<span className="font-anton text-2xl text-brand-red">
									{it.num}
								</span>
								<ArrowUpRight
									className="w-6 h-6 text-brand-navy group-hover:text-white transition-colors duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 transform-gpu transition-transform"
									strokeWidth={2}
								/>
							</div>

							<div className="mt-10 flex items-center gap-4">
								<div className="w-11 h-11 border border-brand-navy group-hover:border-white flex items-center justify-center flex-shrink-0 transition-colors">
									<it.icon
										className="w-5 h-5 text-brand-navy group-hover:text-white transition-colors"
										strokeWidth={2}
									/>
								</div>
								<h3 className="font-chivo font-black uppercase text-2xl sm:text-3xl leading-none text-brand-navy group-hover:text-white transition-colors">
									{it.title}
								</h3>
							</div>

							<p className="mt-5 font-plex text-sm sm:text-[15px] leading-relaxed text-brand-navy/75 group-hover:text-white/80 transition-colors">
								{it.copy}
							</p>

							<div className="absolute bottom-6 left-6 sm:left-8 font-chivo text-[11px] uppercase tracking-[0.3em] font-bold text-brand-navy/50 group-hover:text-brand-red transition-colors">
								Tap to call
							</div>
						</motion.a>
					))}
				</div>
			</div>
		</section>
	);
};

export default Services;
